import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  Optional,
} from "@angular/core";
import { Subject, Subscription } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { TranslateService } from "@ngx-translate/core";
import { CellEvent, GridOptions, RowNode, TabToNextCellParams } from "ag-grid-community/main";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import {
  BaseTableDefinitionFactory,
  SystemConfigPromiseService,
  TableDefinition,
} from "shared/cubiq-services";
import { BfxBaseBlotterColumnSelectorService } from "../../bfx-base-blotter/bfx-base-blotter-columnselector.service";

export const FWD_ROLL_ROW_HEIGHT: number = 24;

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { FwdRollClientPtsEditor } from "app/bfx-blotter-intervention/fwd-roll-pricing/editors/FwdRollClientPtsEditor";
import { FwdRollFwdMarkupEditor } from "app/bfx-blotter-intervention/fwd-roll-pricing/editors/FwdRollFwdMarkupEditor";
import { FwdRollMarketPtsEditor } from "app/bfx-blotter-intervention/fwd-roll-pricing/editors/FwdRollMarketPtsEditor";
import {
  FwdRollPricingCmd,
  FwdRollPricingCmdPayload,
  RowUpdatePayload,
} from "app/bfx-blotter-intervention/fwd-roll-pricing/models/fwd-roll-pricing-cmd-payload.model";
import { FwdRollPortfolioModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollPortfolioModel";
import { LockRowRenderer } from "app/bfx-blotter-intervention/fwd-roll-pricing/renderers/LockRowRenderer";
import { NativeDesktopSupport } from "../../bfx-native-desktop/native-desktop-support";
import { FwdRollIntentRenderer } from "app/bfx-blotter-intervention/fwd-roll-pricing/renderers/FwdRollIntentRenderer";
import { FwdRollDealtPairModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollDealtPairModel";
import { OrderStatusRenderer } from "app/bfx-blotter-intervention/fwd-roll-pricing/renderers/OrderStatusRenderer";
import { DomHandler } from "@bfx/primeng/dom";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { AgGridEditableBodyScroll } from "../../shared/ag-grid/AgGridEditableBodyScroll";
import {
  COL_ADJUSTED_PTS_PERCENT_CHANGE,
  COL_AGREED_PTS,
} from "../../../shared/cubiq-services/model/FwdRollBaseTableDefinitionFactory";

@Component({
  selector: "bfx-fwd-roll-base-grid",
  template: "<div></div>",
  standalone: false,
})
export class BfxFwdRollBaseBlotterComponent implements OnDestroy, AfterViewInit {
  public static instanceId = 0;

  public blotterId: string;
  public height: number = 500;

  @Input("portfolioModel")
  public portfolioModel: FwdRollPortfolioModel;

  protected primed: boolean = false;
  protected tableData: any = null;
  public restored: boolean = false;
  @Input("columnsConfig")
  public columnsConfiguration: any = {};

  private sortingModel: any = [{ colId: "selection", sort: "asc" }];
  public defaultSortingModel: any = null;
  public blotterInstance: any = null;

  public tableDefinition: TableDefinition = null;
  public columnDefs: Array<any> = [];
  public frameworkComponents: any = {
    selectionRenderer: LockRowRenderer,
    orderStatusRenderer: OrderStatusRenderer,
    intentRenderer: FwdRollIntentRenderer,
    fwdMarkupSpinner: FwdRollFwdMarkupEditor,
    clientPtsSpinner: FwdRollClientPtsEditor,
    marketPtsSpinner: FwdRollMarketPtsEditor,
  };

  public gridOptions: GridOptions;

  public blotterView: ElementRef;

  private toggleLockedSubject = new Subject<RowNode>();

  public viewInitComplete: boolean = false;

  // Drag Select methods
  // private doingDragSelect: boolean = false;
  // private firstRowToSelect: any;
  // private lastRowToSelect: any;
  // protected selectedIdsBeforeDrag: Array<string> = [];

  public selectedIdsCount: number = 0;
  // skips only on most concrete subclass (e.g. Ladder still calls super{Order,Base} on rows in this set)
  protected suppressNextOnRowSelected: WeakSet<RowNode> = new WeakSet();
  protected suppressNextFilterChangedDeselection = false;

  protected mouseOverCellEvent: CellEvent = null;
  protected nodeMouseUpCountMap: WeakMap<RowNode, number> = new WeakMap();
  protected managerSubscription: Subscription;
  protected toggleLockedSubscription: Subscription;
  protected dsRowHeight: number = 30;

  constructor(
    BfxBlotterElement: ElementRef,
    @Optional() private _tableDefinitionFactory: BaseTableDefinitionFactory,
    protected translationService: TranslateService,
    protected columnSelectorService: BfxBaseBlotterColumnSelectorService,
    protected sysConfigService: SystemConfigPromiseService,
    protected mouseModifierKeyService: BfxMouseModifierKeyService, // private posBookService: PositionBookPromiseLookupService,
    public zone: NgZone,
  ) {
    (this.constructor as any).instanceId++;
    this.blotterId = this.constructor.name; // + "_" + (this.constructor as any).instanceId;
    this.blotterInstance = this;
    this.blotterView = BfxBlotterElement;

    let funcGetRowNodId = function (data) {
      if (data && this.tableDefinition != null)
        return String(data[this.tableDefinition.primaryKey]);
      return "";
    }.bind(this);

    this.gridOptions = <GridOptions>{
      getRowNodeId: funcGetRowNodId,
      rowSelection: "multiple",
      deltaRowDataMode: true,
      alignedGrids: [],
      headerHeight: 24,
      suppressContextMenu: true,
      stopEditingWhenGridLosesFocus: true,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowClickSelection: true,
      suppressScrollOnNewData: true,
      suppressTabbing: false,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: false,
      treeData: true,
      rowDeselection: false,
      enableBrowserTooltips: true,

      onGridReady: (params) => {
        const doc = DomHandler.getDocument(this.blotterView);
        this.dsRowHeight = BFXDomHelper.getVariablePx(doc, "--ds-cont-sizing-common-sm", 30);

        // rest of initialization happens after ag-grid is ready
        if (this.gridOptions != null) {
          this.gridOptions.api = params.api;
          this.gridOptions.columnApi = params.columnApi;

          if (this.gridOptions.api != null) {
            this.gridOptions.api.showLoadingOverlay();
          }
        }

        this._tableDefinitionFactory.defineTable().then(
          (value) => {
            this.tableDefinition = value;
            this.initializeColumnDefinitions();
            this.gridOptions.api.setRowData(this.getGridDataModel());

            // this.dataAddedSubscription = this.portfolioModel.addedSubject.subscribe(
            //   this.onAddedEvent.bind(this),
            // );
            // this.dataRemovedSubscription = this.portfolioModel.removedSubject.subscribe(
            //   this.onRemovedEvent.bind(this),
            // );
            // this.dataChangedSubscription = this.portfolioModel.dataChangedSubject.subscribe(
            //   this.onUpdateEvent.bind(this),
            // );
            // this.pendingChangedSubscription = this.portfolioModel.pendingChangeSubject.subscribe(
            //   ({ deselectIds, selectIds }) => {
            //     this.onPendingUpdateEvent(deselectIds, selectIds);
            //   },
            // );
          },
          (reason: string) => {
            logger.out.info(
              "RawDataOrderAdapter:_tableDefinitionFactory.defineTabl rejection: " + reason,
            );
          },
        );
      },

      tabToNextCell: (params: TabToNextCellParams) => {
        if (
          params.nextCellPosition &&
          params.nextCellPosition.column &&
          params.nextCellPosition.column.getColDef().editable
        ) {
          let col = this.gridOptions.columnApi.getDisplayedColAfter(params.nextCellPosition.column);
          if (col) {
            this.gridOptions.api.ensureColumnVisible(col);
          } else {
            this.gridOptions.api.ensureColumnVisible(params.nextCellPosition.column);
          }
        }
        if (params.nextCellPosition) {
          return {
            rowIndex: params.nextCellPosition.rowIndex,
            column: params.nextCellPosition.column,
            rowPinned: params.nextCellPosition.rowPinned,
          };
        }
        return null;
      },
      onBodyScroll: AgGridEditableBodyScroll(this),
      // onCellMouseOver: (event: CellMouseOverEvent) => {
      //   this.onCellMouseOver(event);
      // },
      // onCellMouseDown: (...events) => {
      //   // only handle after eventloop- forces mouseover logic to run after mousedown
      //   setImmediate(() => {
      //     this.onCellMouseDown.call(this, ...events);
      //   });
      // },
      // onColumnMoved: () => {
      //   // need to update the grid layout
      //   this.gridLayoutChangeEmitter.emit();
      // },
      // onColumnVisible: () => {
      //   // need to update the grid layout
      //   this.gridLayoutChangeEmitter.emit();
      // },
      getDataPath: (data) => {
        return data.key.split("-");
      },
      groupDefaultExpanded: -1,
      groupSuppressAutoColumn: true,
      isRowSelectable: (row) => {
        if (
          !row.data ||
          !row.data.isEditable ||
          row.data.spotAggregateFg ||
          row.data.settleTenor == CubiqTS.Tenor.SPOT
        ) {
          return false;
        }
        return true;
      },
    };
    this.gridOptions.rowClassRules = {
      row_spot_aggregate: (params) => params.data?.spotAggregateFg,
      fwd_roll_hidden: (params) => params.data?.hidden,
    };

    this.gridOptions.context = {
      toggleLocked: this.toggleLockedSubject,
      /** FF Orders have some additional columns, and modified behavior in Market Pts. */
      hasForwardFirstOrder: () => this?.portfolioModel?.forwardFirstOrder === true,
    };

    NativeDesktopSupport.prepareGridOptions(this.gridOptions, BfxBlotterElement.nativeElement);
  }

  ngAfterViewInit() {
    this.viewInitComplete = true;
    if (this.portfolioModel) {
      this.subscribeToManager();
    } else {
      logger.out.error("Cannot subscribe to portfolio changes - not available");
    }
  }

  /**
   * Listen to application events coming from the PortfolioModel - when overridng this method
   * ensure that the contents of the closure here is reproduced.
   */
  protected subscribeToManager(): void {
    this.managerSubscription = this.portfolioModel.interfaceCmd$.subscribe(
      ($event: FwdRollPricingCmdPayload) => {
        switch ($event.cmd) {
          case FwdRollPricingCmd.START:
            logger.out.info("Rebinding data");
            this.rebindData();
            break;
          case FwdRollPricingCmd.LOCKED_STATE_CHANGED:
            if ($event.rowType === this.getRowType()) {
              logger.out.info("Refreshing lock state");
              this.gridOptions.api.refreshCells({ columns: ["selection"] });
            }
            break;
          case FwdRollPricingCmd.ROWS_UPDATED:
            if ($event.rowType === this.getRowType()) {
              const payload = $event.payload as RowUpdatePayload;
              this.refreshCells(payload);
            }
            break;
          case FwdRollPricingCmd.PRICING_SUCCESS:
          case FwdRollPricingCmd.PORTFOLIO_UNLOCKED:
            // Refresh Cells will not get rid of selection checkboxes
            this.gridOptions.api.stopEditing(true);
            this.gridOptions.api.redrawRows();
            break;
          case FwdRollPricingCmd.EDITOR_DISABLED_CHANGED:
            const editors = this.gridOptions.api.getCellEditorInstances();
            if (editors.length > 0) {
              for (const editor of editors) {
                const rollEditor: any = editor;
                if (rollEditor.updateLocked) {
                  rollEditor.updateLocked();
                }
              }
            }
            break;
          case FwdRollPricingCmd.SERVER_ROW_UPDATED:
            if ($event.rowType === this.getRowType()) {
              const payload = $event.payload as RowUpdatePayload;
              this.updateSingleRow(payload);
            }
            break;
          case FwdRollPricingCmd.SERVER_ROW_DELETED:
          case FwdRollPricingCmd.SERVER_ROW_ADDED:
            if ($event.rowType === this.getRowType()) {
              this.rebindData();
            }
            break;
          case FwdRollPricingCmd.DISABLE_FOR_PRICING:
          case FwdRollPricingCmd.UNLOCK_SUCCESS:
          case FwdRollPricingCmd.PORTFOLIO_REFRESH_ERROR:
            this.gridOptions.api.stopEditing(true);
            this.gridOptions.api.redrawRows();
            break;
          default:
            break;
        }
      },
    );
    this.toggleLockedSubscription = this.toggleLockedSubject.subscribe((rowNode) => {
      this.toggleSelectedLockStates(rowNode);
    });
  }

  /**
   * When the values of a singlew row have been changed externally then, in addition tro refreshing
   * cells we will also have to refresh the active editor if it is currently being edited as a model change
   * is not refreshed in an editor.
   * At the moment, we don't bother checking to see if the edited row is changed, just that the edited
   * value is still the same
   * @param payload
   */
  private updateSingleRow(payload: RowUpdatePayload) {
    this.refreshCells(payload);

    const api = this.gridOptions.api;
    // If we've updated data that's currently being edited we may have to restart our editor if the
    // value in the editor is different from the model value now
    const editingPositions = api.getEditingCells();
    if (editingPositions.length > 0) {
      for (const position of editingPositions) {
        const node = api.getDisplayedRowAtIndex(position.rowIndex);
        const modelValue = api.getValue(position.column, node);
        const editor = api.getCellEditorInstances({ rowNodes: [node], columns: [position.column] });
        if (editor.length == 1) {
          const editorValue = editor[0].getValue();
          if (
            !modelValue ||
            !editorValue ||
            (modelValue instanceof CubiqTS.BigNumber &&
              editorValue instanceof CubiqTS.BigNumber &&
              !modelValue.isEqualTo(editorValue))
          ) {
            // VAlues are different (or at least one is null)
            logger.out.info(
              `Editor value (${editorValue.toString()}) and model value (${modelValue.toString()}) are different- restarting editor`,
            );
            api.stopEditing(true);
            api.startEditingCell({ rowIndex: position.rowIndex, colKey: position.column });
          } else {
            logger.out.info("Editor value and model value are the same");
          }
        }
      }
    }
  }

  private refreshCells(payload: RowUpdatePayload) {
    if (payload && payload.updatedRows) {
      for (const rowData of payload.updatedRows) {
        const rowId = rowData.row.rowId;
        const node = this.gridOptions.api.getRowNode(rowId);
        if (node && rowData.columnNames?.length) {
          this.gridOptions.api.refreshCells({ columns: rowData.columnNames, rowNodes: [node] });
        }
      }
    }
  }

  /**
   * Should be overriden by data-specifc blotter ot provide the datamodel
   * @returns
   */
  protected getGridDataModel() {
    return [];
  }

  protected initializeColumnDefinitions(): void {
    this.applyColumnDefinitions();
  }

  protected applyColumnDefinitions(): void {
    let defs: Array<any> = this.tableDefinition.columns;
    defs.forEach((col: any) => {
      if (this.columnsConfiguration) {
        let colConfig: any = this?.columnsConfiguration[col.field];
        if (!colConfig) {
          return;
        }

        if (colConfig?.width) {
          if (col.maxWidth && col.maxWidth < colConfig.width) {
            col.width = col.maxWidth;
          }
          if (col.minWidth && col.minWidth > colConfig.width) {
            col.width = col.minWidth;
          } else {
            col.width = colConfig.width;
          }
        }

        if (colConfig.visible != undefined) {
          col.hide = !colConfig.visible;
        }

        if (colConfig.position != undefined) {
          col.position = colConfig.position;
        }
      }

      col.suppressKeyboardEvent = function (event) {
        if (event && event.event && (event.event.code == "Tab" || event.event.code == "Enter")) {
          return false;
        }
        return event.editing;
      };
      col.suppressNavigable = function (_params) {
        return false;
      };
    });

    if (!this.gridOptions.context.hasForwardFirstOrder()) {
      defs = defs.filter((col) => {
        return !(COL_AGREED_PTS === col?.field || COL_ADJUSTED_PTS_PERCENT_CHANGE == col?.field);
      });
    }

    //defs.splice(defs.length - 1, 1);
    defs.sort((a, b) => {
      return a.position - b.position;
    });
    this.columnDefs = defs;
  }

  ngOnDestroy() {
    if (this.managerSubscription) {
      this.managerSubscription.unsubscribe();
    }
    if (this.toggleLockedSubscription) {
      this.toggleLockedSubscription.unsubscribe();
    }
  }

  public getComponentConfiguration(): any {
    return {
      blotterId: this.blotterId,
      columnsConfiguration: this.getBlotterColumnsConfigs(),
      sortingModel: this.getBlotterSortingModel(),
    };
  }

  public restoreFromConfiguration(config: any): void {
    if (config) {
      this.columnsConfiguration = config.columnsConfiguration;
      this.sortingModel = config.sortingModel;
      this.restored = true;
    }
    if (this.tableDefinition) {
      this.applyColumnDefinitions();
    }
  }

  // handle the click of the ag-grid column header
  public handleClick(event) {
    this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
  }

  public getBlotterColumnsConfigs(): any {
    if (
      this.gridOptions == null ||
      this.gridOptions.columnApi == null ||
      typeof this.gridOptions.columnApi.getAllGridColumns !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // column config won't be restored but defaults will still apply
      return null;
    }
    let columnsToSave = {};
    let positionCounter = 0;
    this.gridOptions.columnApi.getAllGridColumns().forEach((gridCol) => {
      let column = {
        id: "",
        width: 150,
        position: 1,
        visible: true,
      };
      column.id = gridCol.getColId();
      column.width = gridCol.getActualWidth();
      column.visible = gridCol.isVisible();
      column.position = positionCounter;

      columnsToSave[gridCol.getColId()] = column;
      positionCounter++;
    });
    this.columnsConfiguration = columnsToSave;
    return columnsToSave;
  }

  //this can be moved to base once the ag grid has being implemented
  public getBlotterSortingModel(): Array<any> {
    // this.sortingModel = this.gridOptions.api.getSortModel();
    if (
      this.gridOptions == null ||
      this.gridOptions.api == null ||
      typeof this.gridOptions.api.getSortModel !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // sort model won't be restored but defaults will still apply
      return null;
    }

    return this.sortingModel;
  }

  protected selectedRowsLength(): number {
    return this.gridOptions.api.getSelectedRows().length;
  }

  public rebindData() {
    if (this.gridOptions?.api) {
      setImmediate(() => this.gridOptions.api.setRowData(this.getGridDataModel()));
    }
  }

  public getRowHeight(_params) {
    return FWD_ROLL_ROW_HEIGHT;
  }

  public getRowType(): CubiqTS.FwdRollRowType {
    throw new Error("Must be Overridden");
  }

  /**
   * Process a rowNode that has been clicked and toggle the locked state of that row
   * and all other selected rows
   * @param row
   */
  protected toggleSelectedLockStates(row: RowNode) {
    logger.out.info("Got toggle lock");
    const data = row.data as FwdRollDealtPairModel;
    if (!this.portfolioModel.disabled && !data.isEditorDisabled) {
      const isLocked = data.lockedForUserPricing;

      this.portfolioModel.confirmLockedForUserPricing(data, !isLocked);
    } else {
      logger.out.info("Disabling log while editing disabled for this row");
    }
  }
}
